import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { permissionGroupsService } from '../services/api/permissionGroups';

interface PermissionContextType {
  permissions: string[];
  isLoading: boolean;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const userPermissions = await permissionGroupsService.getMyPermissions();
      setPermissions(userPermissions);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, user]);

  const hasPermission = useCallback(
    (permissionCode: string) => {
      // Admin always has all permissions
      if (user?.role === 'ADMIN') return true;
      return permissions.includes(permissionCode);
    },
    [permissions, user]
  );

  const hasAnyPermission = useCallback(
    (permissionCodes: string[]) => {
      // Admin always has all permissions
      if (user?.role === 'ADMIN') return true;
      return permissionCodes.some((code) => permissions.includes(code));
    },
    [permissions, user]
  );

  const hasAllPermissions = useCallback(
    (permissionCodes: string[]) => {
      // Admin always has all permissions
      if (user?.role === 'ADMIN') return true;
      return permissionCodes.every((code) => permissions.includes(code));
    },
    [permissions, user]
  );

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        isLoading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Permission codes for the application
 */
export const PERMISSIONS = {
  // Employees
  EMPLOYEES_VIEW: 'employees.view',
  EMPLOYEES_CREATE: 'employees.create',
  EMPLOYEES_EDIT: 'employees.edit',
  EMPLOYEES_DELETE: 'employees.delete',
  EMPLOYEES_IMPORT: 'employees.import',
  EMPLOYEES_EXPORT: 'employees.export',

  // Users
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_EDIT: 'users.edit',
  USERS_MANAGE: 'users.manage',

  // Departments
  DEPARTMENTS_VIEW: 'departments.view',
  DEPARTMENTS_CREATE: 'departments.create',
  DEPARTMENTS_EDIT: 'departments.edit',
  DEPARTMENTS_DELETE: 'departments.delete',

  // Leave Requests
  LEAVE_REQUESTS_VIEW_OWN: 'leave_requests.view_own',
  LEAVE_REQUESTS_VIEW_TEAM: 'leave_requests.view_team',
  LEAVE_REQUESTS_VIEW_ALL: 'leave_requests.view_all',
  LEAVE_REQUESTS_CREATE: 'leave_requests.create',
  LEAVE_REQUESTS_EDIT: 'leave_requests.edit',
  LEAVE_REQUESTS_APPROVE: 'leave_requests.approve',
  LEAVE_REQUESTS_EXPORT: 'leave_requests.export',

  // Leave Balances
  LEAVE_BALANCES_VIEW_OWN: 'leave_balances.view_own',
  LEAVE_BALANCES_VIEW_TEAM: 'leave_balances.view_team',
  LEAVE_BALANCES_VIEW_ALL: 'leave_balances.view_all',
  LEAVE_BALANCES_EDIT: 'leave_balances.edit',

  // Reports
  REPORTS_HEADCOUNT: 'reports.headcount',
  REPORTS_LEAVE_SUMMARY: 'reports.leave_summary',
  REPORTS_LATE_SUMMARY: 'reports.late_summary',
  REPORTS_APPROVAL_SLA: 'reports.approval_sla',
  REPORTS_EXPORT: 'reports.export',

  // Settings
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',

  // Audit
  AUDIT_VIEW: 'audit.view',
  AUDIT_EXPORT: 'audit.export',

  // Permission Groups
  GROUPS_VIEW: 'groups.view',
  GROUPS_CREATE: 'groups.create',
  GROUPS_EDIT: 'groups.edit',
  GROUPS_DELETE: 'groups.delete',
  GROUPS_ASSIGN: 'groups.assign',
} as const;
